import { visibleReportDecks } from '../utils/reportVisibility';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { useCallback, useEffect, useState } from 'react';
import { PnlDataVersion, VersionChoices } from 'src/components/star-grid/reportSelectors.types';
import { isResourcePending, useAsyncResource } from 'src/components/star-grid/hooks/useAsyncResource';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { listPnlDecks, listPnlVersions } from 'src/components/star-grid/services/starGridService';
import { PnlReportConfig } from 'src/components/star-grid/starGridTypes';
import { deckOptions, selectedVersionOption, versionOptions } from 'src/components/star-grid/utils/reportSelectorOptions';

export interface ReportSelectorControls {
  readonly version: SelectProps;
  readonly versionChoices: VersionChoices;
  readonly deck: SelectProps;
}
interface SelectorProps {
  readonly reportId?: number;
  readonly definition?: PnlReportConfig;
  readonly asOfTimestamp?: string;
  readonly deckType?: string;
  readonly onVersionChange: (timestamp?: string) => void;
  readonly onDeckChange?: (deckType?: string) => void;
  readonly onOpenReport?: (reportId: number) => void;
}
export interface CatalogMessages {
  readonly placeholder: string;
  readonly loadingText: string;
  readonly errorText: string;
  readonly empty: string;
}

/** Exported so the report viewer's Version picker is the same catalogue, not a parallel one. */
/** `eager: false` leaves the load to `load()`, for a catalog nobody needs until its picker opens. */
export const useSelectorCatalog = <T>(fetcher: () => Promise<T>, messages: CatalogMessages, reportId?: number, eager = true) => {
  const text = useStarGridText();
  const resource = useAsyncResource(fetcher);
  useEffect(() => {
    if (eager) resource.ensureLoaded();
  }, [resource.ensureLoaded, reportId, eager]);
  const control: Partial<SelectProps> = {
    ...messages,
    filteringType: 'auto',
    filteringPlaceholder: text('findValue'),
    expandToViewport: true,
    statusType: isResourcePending(resource) ? 'loading' : resource.error ? 'error' : 'finished',
    recoveryText: text('retryCatalog'),
    noMatch: text('noMatchingValues'),
    onLoadItems: ({ detail }) => {
      if (resource.error && detail.samePage) resource.refresh();
    }
  };
  return { data: resource.data, control, retry: resource.refresh, load: resource.ensureLoaded };
};

const useVersionCatalog = (messages: CatalogMessages, reportId?: number) => {
  const [historyIncomplete, setHistoryIncomplete] = useState(false);
  const fetchVersions = useCallback(async () => {
    setHistoryIncomplete(false);
    return listPnlVersions(() => setHistoryIncomplete(true));
  }, []);
  const catalog = useSelectorCatalog(fetchVersions, messages, reportId);
  return { ...catalog, historyIncomplete };
};

const useVersionSelector = (props: SelectorProps): { control: SelectProps; choices: VersionChoices } => {
  const text = useStarGridText();
  const catalog = useVersionCatalog(
    {
      placeholder: text('chooseVersion'),
      loadingText: text('loadingVersions'),
      errorText: text('versionsUnavailable'),
      empty: text('noVersions')
    },
    props.reportId ?? props.definition?.pnl_report_id
  );
  const versions = catalog.data ?? [];
  const select = (version: PnlDataVersion): void => {
    if (props.definition && version.resolvable !== false) props.onVersionChange(version.as_of ?? undefined);
  };
  const control: SelectProps = {
    ...catalog.control,
    options: versionOptions(versions, text('versionHistoryUnavailable')),
    selectedOption: selectedVersionOption(versions, props.asOfTimestamp),
    disabled: !props.definition,
    onChange: ({ detail }) => {
      const selected = versions.find((item) => item.version === detail.selectedOption.value);
      if (selected) select(selected);
    }
  };
  return { control, choices: { versions, select, retry: catalog.retry, historyIncomplete: catalog.historyIncomplete } };
};

const useDeckSelector = (props: SelectorProps): SelectProps => {
  const text = useStarGridText();
  const catalog = useSelectorCatalog(
    listPnlDecks,
    {
      placeholder: text('chooseDeck'),
      loadingText: text('loadingDecks'),
      errorText: text('decksUnavailable'),
      empty: text('noDecks')
    },
    props.reportId ?? props.definition?.pnl_report_id
  );
  const options = deckOptions(visibleReportDecks(catalog.data ?? []), props.definition, text('unassignedDeck'));
  const current = props.definition;
  // Selection is keyed by deck TYPE now: the report renders as whichever deck is active (the
  // runtime override, else its own stored deck), not by which report row is open.
  const activeDeck = (props.deckType ?? current?.deck_type ?? '').trim().toLowerCase();
  const fallback = current ? { value: activeDeck, label: current.deck_type ?? text('unassignedDeck') } : null;
  return {
    ...catalog.control,
    options,
    selectedOption: options.find((option) => option.value === activeDeck) ?? fallback,
    disabled: !current || !props.onDeckChange,
    onChange: ({ detail }) => {
      const selected = options.find((option) => option.value === detail.selectedOption.value);
      if (selected?.value) props.onDeckChange?.(selected.value);
    }
  };
};

export const useReportSelectors = (props: SelectorProps): ReportSelectorControls => {
  const version = useVersionSelector(props);
  return { version: version.control, versionChoices: version.choices, deck: useDeckSelector(props) };
};
